import React, { useEffect, useMemo, useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts';
import { Save, RefreshCcw } from 'lucide-react';
import { money, dateLabel, loadSiteSettings, saveSiteSettings } from '../lib/compat.js';
import { db, fs } from '../lib/firebase.js';
import { Metric, orderStatus, orderTotal, STATUS_LABELS } from './shared.jsx';
import { CircleDollarSign, Package, Users, BarChart3, AlertTriangle } from 'lucide-react';

const COLORS = ['#2F338C', '#F8D72F', '#1a7a3e', '#185FA5', '#A32D2D', '#8E44AD', '#E67E22'];

// ─── GA4 JWT helper ─────────────────────────────────────────────────────────
async function getGA4AccessToken(serviceAccountJson) {
  const sa = typeof serviceAccountJson === 'string' ? JSON.parse(serviceAccountJson) : serviceAccountJson;
  const now = Math.floor(Date.now() / 1000);
  const header = { alg: 'RS256', typ: 'JWT' };
  const payload = {
    iss: sa.client_email,
    scope: 'https://www.googleapis.com/auth/analytics.readonly',
    aud: 'https://oauth2.googleapis.com/token',
    iat: now,
    exp: now + 3600
  };

  function b64url(str) { return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, ''); }
  function b64urlBuf(buf) { return b64url(String.fromCharCode(...new Uint8Array(buf))); }

  const headerB64 = b64url(JSON.stringify(header));
  const payloadB64 = b64url(JSON.stringify(payload));
  const sigInput = `${headerB64}.${payloadB64}`;

  const pemKey = sa.private_key.replace(/-----[^-]+-----/g, '').replace(/\s/g, '');
  const keyBuf = Uint8Array.from(atob(pemKey), (c) => c.charCodeAt(0));
  const cryptoKey = await crypto.subtle.importKey('pkcs8', keyBuf, { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' }, false, ['sign']);
  const sigBuf = await crypto.subtle.sign('RSASSA-PKCS1-v1_5', cryptoKey, new TextEncoder().encode(sigInput));
  const jwt = `${sigInput}.${b64urlBuf(sigBuf)}`;

  const res = await fetch('https://oauth2.googleapis.com/token', {
    method: 'POST',
    body: new URLSearchParams({ grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer', assertion: jwt })
  });
  const data = await res.json();
  if (!data.access_token) throw new Error(data.error_description || 'Failed to get GA4 access token');
  return data.access_token;
}

async function ga4Report(token, propertyId, startDate, endDate, dimensions, metrics) {
  const res = await fetch(`https://analyticsdata.googleapis.com/v1beta/properties/${propertyId}:runReport`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ dateRanges: [{ startDate, endDate }], dimensions: dimensions.map((n) => ({ name: n })), metrics: metrics.map((n) => ({ name: n })) })
  });
  if (!res.ok) { const err = await res.json(); throw new Error(err.error?.message || 'GA4 API failed'); }
  return res.json();
}

function parseGA4Rows(report) {
  return (report.rows || []).map((row) => {
    const obj = {};
    (report.dimensionHeaders || []).forEach((h, i) => { obj[h.name] = row.dimensionValues?.[i]?.value || ''; });
    (report.metricHeaders || []).forEach((h, i) => { obj[h.name] = Number(row.metricValues?.[i]?.value || 0); });
    return obj;
  });
}

export default function AdminAnalytics({ data }) {
  const [gaPeriod, setGaPeriod] = useState('30daysAgo');
  const [ga4Creds, setGa4Creds] = useState(null);
  const [gaData, setGaData] = useState(null);
  const [gaLoading, setGaLoading] = useState(false);
  const [gaError, setGaError] = useState('');
  const [setupOpen, setSetupOpen] = useState(false);
  const [trackingIds, setTrackingIds] = useState({ gaId: '', fbPixelId: '' });
  const [trackMsg, setTrackMsg] = useState('');

  // Load GA4 creds
  useEffect(() => {
    fs.getDoc(fs.doc(db, 'settings', 'ga4api')).then((snap) => {
      if (snap.exists() && snap.data().propertyId && snap.data().serviceAccountJson) {
        setGa4Creds(snap.data());
      }
    }).catch(() => {});
    loadSiteSettings().then((s) => setTrackingIds({ gaId: s?.gaId || '', fbPixelId: s?.fbPixelId || '' })).catch(() => {});
  }, []);

  useEffect(() => {
    if (ga4Creds) fetchGaData();
  }, [ga4Creds, gaPeriod]);

  async function fetchGaData() {
    if (!ga4Creds) return;
    setGaLoading(true);
    setGaError('');
    try {
      const token = await getGA4AccessToken(ga4Creds.serviceAccountJson);
      const pid = ga4Creds.propertyId.replace('properties/', '').trim();
      const [overview, overTime, devices, cities, sources, pages] = await Promise.all([
        ga4Report(token, pid, gaPeriod, 'today', [], ['totalUsers', 'activeUsers', 'sessions', 'bounceRate', 'averageSessionDuration']),
        ga4Report(token, pid, gaPeriod, 'today', ['date'], ['totalUsers', 'sessions']),
        ga4Report(token, pid, gaPeriod, 'today', ['deviceCategory'], ['sessions']),
        ga4Report(token, pid, gaPeriod, 'today', ['city'], ['sessions']),
        ga4Report(token, pid, gaPeriod, 'today', ['sessionDefaultChannelGroup'], ['sessions']),
        ga4Report(token, pid, gaPeriod, 'today', ['pagePath'], ['screenPageViews'])
      ]);
      setGaData({ overview: parseGA4Rows(overview)[0] || {}, overTime: parseGA4Rows(overTime), devices: parseGA4Rows(devices), cities: parseGA4Rows(cities).slice(0, 8), sources: parseGA4Rows(sources).slice(0, 6), pages: parseGA4Rows(pages).slice(0, 8) });
    } catch (e) {
      setGaError(e.message || 'GA4 API failed');
    } finally {
      setGaLoading(false);
    }
  }

  // Store analytics
  const storeMetrics = useMemo(() => {
    const revenue = data.orders.reduce((s, o) => s + Number(o.total || orderTotal(o) || 0), 0);
    const delivered = data.orders.filter((o) => orderStatus(o) === 'delivered').length;
    const failed = data.orders.filter((o) => ['failed', 'cancelled', 'payment_failed'].includes(orderStatus(o))).length;
    const aov = data.orders.length ? revenue / data.orders.length : 0;
    return { revenue, delivered, failed, aov };
  }, [data.orders]);

  const revenueChartData = useMemo(() => {
    const map = {};
    data.orders.forEach((o) => {
      const d = o.createdAt ? new Date(o.createdAt instanceof Object && o.createdAt.toDate ? o.createdAt.toDate() : o.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '';
      if (d) {
        map[d] = map[d] || { date: d, revenue: 0, orders: 0 };
        map[d].revenue += Number(o.total || orderTotal(o) || 0);
        map[d].orders += 1;
      }
    });
    return Object.values(map).slice(-30);
  }, [data.orders]);

  const statusData = useMemo(() => {
    const map = {};
    data.orders.forEach((o) => { const s = STATUS_LABELS[orderStatus(o)] || orderStatus(o); map[s] = (map[s] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [data.orders]);

  const topBooks = useMemo(() => {
    const map = {};
    data.orders.forEach((o) => (o.items || []).forEach((i) => { const k = i.bt || i.bookTitle || 'Book'; map[k] = (map[k] || 0) + Number(i.price || 0) * Number(i.qty || 1); }));
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [data.orders]);

  const topDistricts = useMemo(() => {
    const map = {};
    data.orders.forEach((o) => { const d = o.delivery?.district || ''; if (d) map[d] = (map[d] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [data.orders]);

  const hourlyData = useMemo(() => {
    const map = Array.from({ length: 24 }, (_, i) => ({ hour: i + ':00', orders: 0 }));
    const today = new Date(); today.setHours(0, 0, 0, 0);
    data.orders.forEach((o) => {
      const d = o.createdAt ? new Date(o.createdAt instanceof Object && o.createdAt.toDate ? o.createdAt.toDate() : o.createdAt) : null;
      if (d && d >= today) map[d.getHours()].orders += 1;
    });
    return map;
  }, [data.orders]);

  async function saveTrackingIds() {
    await saveSiteSettings(trackingIds);
    setTrackMsg('Tracking IDs saved.');
    setTimeout(() => setTrackMsg(''), 3000);
  }

  const periodLabels = { today: 'Today', '7daysAgo': '7 Days', '30daysAgo': '30 Days', '90daysAgo': '90 Days' };

  return (
    <div className="adminDashboard">
      {/* GA4 Setup / Connected */}
      {!ga4Creds ? (
        <div style={{ background: 'linear-gradient(135deg,#1A73E8,#4285F4)', borderRadius: 14, padding: '24px 28px', marginBottom: 20, color: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>🔌 Connect Google Analytics to see visitor data</div>
              <div style={{ fontSize: 13, opacity: .85, lineHeight: 1.6, maxWidth: 560 }}>
                To see sessions, users, cities, devices and time-on-page inside this panel you need to paste your <strong>GA4 Property ID</strong> and a <strong>Service Account JSON key</strong> once. Takes about 3 minutes.
              </div>
            </div>
            <button onClick={() => setSetupOpen(true)} style={{ background: '#fff', color: '#1A73E8', border: 'none', padding: '11px 22px', borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>⚙️ Connect Now</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 0, border: '1px solid var(--admin-border)', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
              {Object.entries(periodLabels).map(([val, label]) => (
                <button key={val} onClick={() => setGaPeriod(val)} style={{ padding: '7px 14px', background: gaPeriod === val ? 'var(--admin-navy)' : 'transparent', color: gaPeriod === val ? '#fff' : 'var(--admin-muted)', border: 'none', fontSize: 12, fontWeight: 500, cursor: 'pointer', borderRight: '1px solid var(--admin-border)' }}>{label}</button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#E8F5EE', color: '#1a7a3e', fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 100 }}>
              <span style={{ width: 6, height: 6, background: '#1a7a3e', borderRadius: '50%' }} />
              GA4 Live
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="adminRefreshBtn" onClick={fetchGaData}><RefreshCcw size={13} /> Refresh</button>
            <button onClick={() => setSetupOpen(true)} style={{ padding: '7px 14px', background: '#fff', border: '1px solid var(--admin-border)', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>⚙️ Settings</button>
            <button onClick={() => window.open('https://analytics.google.com', '_blank')} style={{ padding: '7px 14px', background: '#4285F4', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>Open GA4 ↗</button>
          </div>
        </div>
      )}

      {/* GA4 KPI cards */}
      {ga4Creds && (
        <>
          {gaLoading && <div className="adminLoading" style={{ textAlign: 'center', padding: 40 }}>Fetching visitor data from Google Analytics…</div>}
          {gaError && <div className="banner danger" style={{ marginBottom: 16 }}>⚠️ {gaError}</div>}
          {gaData && !gaLoading && (
            <>
              <div className="adminStatsGrid5" style={{ marginBottom: 16 }}>
                {[
                  { label: 'Total Users', val: gaData.overview.totalUsers || 0, sub: 'Unique visitors', color: '#4285F4' },
                  { label: 'Active Users', val: gaData.overview.activeUsers || 0, sub: 'Engaged this period', color: '#34A853' },
                  { label: 'Sessions', val: gaData.overview.sessions || 0, sub: 'Total visits', color: '#FBBC05' },
                  { label: 'Bounce Rate', val: ((gaData.overview.bounceRate || 0) * 100).toFixed(1) + '%', sub: 'Left without action', color: '#EA4335' },
                  { label: 'Avg Session', val: formatDuration(gaData.overview.averageSessionDuration || 0), sub: 'Time on site', color: '#2F338C' }
                ].map(({ label, val, sub, color }) => (
                  <div key={label} className="statCard" style={{ borderTopColor: color }}>
                    <div className="sc-lbl" style={{ color }}>{label}</div>
                    <div className="sc-val" style={{ fontSize: 22 }}>{val}</div>
                    <div className="sc-sub">{sub}</div>
                  </div>
                ))}
              </div>

              <div className="chartGrid" style={{ gridTemplateColumns: '2fr 1fr', marginBottom: 16 }}>
                <div className="chartCard">
                  <h4>Users Over Time</h4>
                  <div style={{ height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={gaData.overTime.map((r) => ({ date: r.date.slice(4), users: r.totalUsers, sessions: r.sessions }))}>
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Area type="monotone" dataKey="users" stroke="#4285F4" fill="#E8F0FE" strokeWidth={2} />
                        <Area type="monotone" dataKey="sessions" stroke="#34A853" fill="#E8F5EC" strokeWidth={1.5} strokeDasharray="5 3" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="chartCard">
                  <h4>📱 Device Category</h4>
                  <div style={{ height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={gaData.devices.map((r) => ({ name: r.deviceCategory, value: r.sessions }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                          {gaData.devices.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div className="adminCard" style={{ margin: 0 }}>
                  <div className="adminCardHead"><h3>📍 Top Cities</h3></div>
                  <div style={{ padding: '12px 16px' }}>
                    {gaData.cities.map((r) => <div key={r.city} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--admin-border)', fontSize: 12 }}><span>{r.city}</span><strong>{r.sessions}</strong></div>)}
                  </div>
                </div>
                <div className="adminCard" style={{ margin: 0 }}>
                  <div className="adminCardHead"><h3>📡 Traffic Sources</h3></div>
                  <div style={{ padding: '12px 16px' }}>
                    {gaData.sources.map((r) => <div key={r.sessionDefaultChannelGroup} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--admin-border)', fontSize: 12 }}><span>{r.sessionDefaultChannelGroup}</span><strong>{r.sessions}</strong></div>)}
                  </div>
                </div>
                <div className="adminCard" style={{ margin: 0 }}>
                  <div className="adminCardHead"><h3>📄 Top Pages</h3></div>
                  <div style={{ padding: '12px 16px' }}>
                    {gaData.pages.map((r) => <div key={r.pagePath} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--admin-border)', fontSize: 12 }}><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>{r.pagePath}</span><strong>{r.screenPageViews}</strong></div>)}
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ── Store Performance ── */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--admin-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>📦 STORE PERFORMANCE (from your orders)</div>
      <div className="adminStatsGrid5" style={{ marginBottom: 16 }}>
        <Metric label="Revenue" value={money(storeMetrics.revenue)} icon={<CircleDollarSign size={18} />} tone="gold" />
        <Metric label="Orders" value={data.orders.length} icon={<Package size={18} />} />
        <Metric label="Delivered" value={storeMetrics.delivered} icon={<Package size={18} />} tone="green" />
        <Metric label="Avg Order Value" value={money(storeMetrics.aov)} icon={<BarChart3 size={18} />} tone="blue" />
        <Metric label="Failed Payments" value={storeMetrics.failed} icon={<AlertTriangle size={18} />} tone="red" />
      </div>

      <div className="chartGrid" style={{ gridTemplateColumns: '2fr 1fr', marginBottom: 16 }}>
        <div className="chartCard">
          <h4>Revenue &amp; Orders Over Time</h4>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueChartData}>
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="rev" tick={{ fontSize: 10 }} tickFormatter={(v) => '₹' + (v / 1000).toFixed(0) + 'k'} />
                <YAxis yAxisId="ord" orientation="right" tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v, n) => n === 'revenue' ? money(v) : v} />
                <Area yAxisId="rev" type="monotone" dataKey="revenue" stroke="#2F338C" fill="#E8EAFF" strokeWidth={2} />
                <Area yAxisId="ord" type="monotone" dataKey="orders" stroke="#1a7a3e" fill="none" strokeWidth={1.5} strokeDasharray="4 2" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="chartCard">
          <h4>Order Status</h4>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="adminCard" style={{ margin: 0 }}>
          <div className="adminCardHead"><h3>🏆 Top Books by Revenue</h3></div>
          <div style={{ padding: '12px 16px' }}>
            {topBooks.map(([name, val]) => <div key={name} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--admin-border)', fontSize: 12 }}><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{name}</span><strong>{money(val)}</strong></div>)}
          </div>
        </div>
        <div className="chartCard" style={{ margin: 0 }}>
          <h4>Orders by Hour — Today</h4>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyData}>
                <XAxis dataKey="hour" tick={{ fontSize: 9 }} interval={2} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="orders" fill="#2F338C" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="adminCard" style={{ margin: 0 }}>
          <div className="adminCardHead"><h3>📍 Top Districts (Orders)</h3></div>
          <div style={{ padding: '12px 16px' }}>
            {topDistricts.map(([name, val]) => <div key={name} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--admin-border)', fontSize: 12 }}><span>{name}</span><strong>{val}</strong></div>)}
          </div>
        </div>
      </div>

      {/* Tracking IDs */}
      <div className="adminCard">
        <div className="adminCardHead"><h3>⚙️ Tracking IDs</h3><span style={{ fontSize: 12, color: 'var(--admin-muted)' }}>Saved to Firestore settings/site</span></div>
        <div style={{ padding: '18px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--admin-muted)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.05em' }}>GA4 Measurement ID</span>
            <input value={trackingIds.gaId} placeholder="G-XXXXXXXXXX" onChange={(e) => setTrackingIds({ ...trackingIds, gaId: e.target.value.trim() })} style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--admin-border)', borderRadius: 8, fontSize: 13, fontFamily: 'monospace', outline: 'none' }} />
          </div>
          <div>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--admin-muted)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.05em' }}>Facebook Pixel ID</span>
            <input value={trackingIds.fbPixelId} placeholder="1234567890123" onChange={(e) => setTrackingIds({ ...trackingIds, fbPixelId: e.target.value.trim() })} style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--admin-border)', borderRadius: 8, fontSize: 13, fontFamily: 'monospace', outline: 'none' }} />
          </div>
          <div style={{ gridColumn: '1/-1', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn-primary" onClick={saveTrackingIds}><Save size={14} /> Save Tracking IDs</button>
            <button onClick={() => setTrackingIds({ ...trackingIds, gaId: '' })} style={{ padding: '8px 14px', background: '#fff', border: '1px solid var(--admin-border)', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>Remove GA ID</button>
            <button onClick={() => setTrackingIds({ ...trackingIds, fbPixelId: '' })} style={{ padding: '8px 14px', background: '#fff', border: '1px solid var(--admin-border)', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>Remove FB Pixel</button>
            {trackMsg && <span style={{ fontSize: 13, color: 'var(--admin-green)', fontWeight: 600 }}>{trackMsg}</span>}
          </div>
        </div>
      </div>

      {/* GA4 Setup Modal */}
      {setupOpen && <GA4SetupModal ga4Creds={ga4Creds} onClose={() => setSetupOpen(false)} onSaved={(creds) => { setGa4Creds(creds); setSetupOpen(false); fetchGaData(); }} />}
    </div>
  );
}

function formatDuration(seconds) {
  if (!seconds) return '0s';
  const s = Math.round(seconds);
  if (s < 60) return s + 's';
  return Math.floor(s / 60) + 'm ' + (s % 60) + 's';
}

function GA4SetupModal({ ga4Creds, onClose, onSaved }) {
  const [form, setForm] = useState({ propertyId: ga4Creds?.propertyId || '', serviceAccountJson: ga4Creds?.serviceAccountJson ? JSON.stringify(ga4Creds.serviceAccountJson, null, 2) : '' });
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!form.propertyId.trim()) { setMsg('Enter a Property ID.'); return; }
    let parsed;
    try { parsed = JSON.parse(form.serviceAccountJson); } catch { setMsg('Invalid JSON in service account key.'); return; }
    if (!parsed.client_email || !parsed.private_key) { setMsg('Service account JSON must have client_email and private_key.'); return; }
    setBusy(true);
    try {
      const payload = { propertyId: form.propertyId.trim(), serviceAccountJson: parsed, updatedAt: new Date().toISOString() };
      await fs.setDoc(fs.doc(db, 'settings', 'ga4api'), payload, { merge: true });
      onSaved(payload);
    } catch (e) {
      setMsg(e.message || 'Failed to save.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="adminModalLayer" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="adminModal adminModal--wide">
        <div className="adminModalHead">
          <h3>🔌 GA4 API Setup</h3>
          <button type="button" onClick={onClose}>✕</button>
        </div>
        <div className="adminModalBody">
          {msg && <div className="banner danger compact">{msg}</div>}
          <div style={{ background: '#E8F0FE', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#1A73E8', marginBottom: 14 }}>
            <strong>How to connect:</strong> Go to <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer">Google Cloud Console</a> → Create Service Account → Grant "Viewer" role → Download JSON key. Then add the service account email to your GA4 property as Viewer. Paste the Property ID (numbers only, from GA4 Admin) and the JSON key below.
          </div>
          <div className="adminFormGrid">
            <label className="full">GA4 Property ID (e.g. 123456789)<input value={form.propertyId} onChange={(e) => setForm({ ...form, propertyId: e.target.value })} placeholder="123456789" style={{ fontFamily: 'monospace' }} /></label>
            <label className="full">
              Service Account JSON Key
              <textarea value={form.serviceAccountJson} onChange={(e) => setForm({ ...form, serviceAccountJson: e.target.value })} placeholder='{"type":"service_account","project_id":"...","private_key":"...","client_email":"..."}' style={{ minHeight: 140, fontFamily: 'monospace', fontSize: 11 }} />
            </label>
          </div>
          <div className="modalActions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="button" className="btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save & Connect'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
